import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useId, useMemo, useState } from 'react';

import { InfoCallout } from '@/components/ui/info-callout';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSuggestions } from '@/hooks/useIntegrationSuggestions';
import { cn } from '@/lib/utils';
import { useGlobalState } from '@/state/global.state';
import { Integration } from '@/types';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Resolves an integration key (e.g. "amazon") to a human-readable name
 * (e.g. "AWS") using the AllIntegrations registry.
 */
function integrationDisplayName(key: string): string {
  const meta = AllIntegrations[key as Integration];
  return meta?.name ?? key;
}

/**
 * Bottom-docked, collapsed-by-default summary bar for suggested (unconfigured)
 * integrations. Expands to reveal one dismissible red nudge per provider.
 *
 * Data + dismissal semantics are unchanged from the previous version:
 * - `useIntegrationSuggestions` reads integration-suggestion Setting records.
 * - Dismissals are persisted per-suggestion via useStorage (tenant-scoped,
 *   cross-tab synced) so a dismissed nudge doesn't reappear after reload.
 * When every visible suggestion is dismissed, the whole bar unmounts.
 */
export function IntegrationSuggestionBanner() {
  const { suggestions } = useIntegrationSuggestions();
  const { drawerState } = useGlobalState();
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const [dismissed, setDismissed] = useStorage<string[]>(
    { key: StorageKey.DISMISSED_INTEGRATION_SUGGESTIONS },
    []
  );

  const dismissedSet = useMemo(() => new Set(dismissed), [dismissed]);

  const visible = useMemo(
    () => suggestions.filter(name => !dismissedSet.has(name)),
    [suggestions, dismissedSet]
  );

  const handleDismiss = useCallback(
    (name: string) => {
      setDismissed(prev => [...(prev ?? []), name]);
    },
    [setDismissed]
  );

  const handleConnect = useCallback(
    (name: string) => {
      const meta = AllIntegrations[name as Integration];
      if (meta) {
        drawerState.handleOpenIntegrationDrawer(meta, 'All');
      }
    },
    [drawerState]
  );

  if (visible.length === 0) return null;

  const count = visible.length;

  return (
    <div className="border-default bg-chariot-layer1 w-full shrink-0 border-t">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? listId : undefined}
        onClick={() => setExpanded(prev => !prev)}
        className="border-chariot-stroke-critical text-chariot-text-primary focus-visible:ring-chariot-stroke-critical flex min-h-12 w-full items-center gap-3 border-l-4 px-4 text-left text-sm focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
      >
        <ExclamationTriangleIcon className="text-chariot-stroke-critical size-5 shrink-0" />
        <span className="min-w-0">
          <span className="text-chariot-stroke-critical font-semibold">
            {count} integration suggestion{count === 1 ? '' : 's'}
          </span>{' '}
          — connect {count === 1 ? 'it' : 'them'} to enable the Correlation
          Engine
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'text-chariot-text-secondary ml-auto size-4 shrink-0 transition-transform duration-200',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {expanded && (
        <div
          id={listId}
          className="flex max-h-64 flex-col gap-2 overflow-y-auto px-3 pt-1 pb-3"
        >
          {visible.map(name => (
            <InfoCallout
              key={name}
              variant="error"
              icon={<ExclamationTriangleIcon className="size-5" />}
              onDismiss={() => handleDismiss(name)}
              dismissLabel={`Dismiss ${integrationDisplayName(name)} suggestion`}
            >
              <p>
                Some of your assets appear to be hosted on{' '}
                <span className="font-semibold">
                  {integrationDisplayName(name)}
                </span>
                .{' '}
                <button
                  type="button"
                  className="font-medium underline hover:no-underline"
                  onClick={() => handleConnect(name)}
                >
                  Connect this integration
                </button>{' '}
                to enable Guard&apos;s Correlation Engine.
              </p>
            </InfoCallout>
          ))}
        </div>
      )}
    </div>
  );
}
